import * as React from 'react';
import { Button } from '../../../components/Button/Button';
import { PhoneVerificationForm } from './PhoneVerificationForm/PhoneVerificationForm';
import { EmailVerificationForm } from './EmailVerificationForm/EmailVerificationForm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';

interface Props {
  onSubmit: () => void;
  onCancel: () => void;

  email: string;
  phone: string;

  onSendEmailCodeAgainClick: (event: any) => void;
  onChangeEmailClick: (event: any) => void;
  onSendPhoneCodeAgainClick: (event: any) => void;
  onChangePhoneClick: (event: any) => void;

  isPhoneConfirmed: boolean;
  isEmailConfirmed: boolean;

  onSubmitEmailClick: (data: { emailCode: string }) => void;
  onSubmitPhoneClick: (data: { phoneCode: string }) => void;

  emailResendError?: string;
  emailVerifyError?: string;
  phoneResendError?: string;
  phoneVerifyError?: string;

  emailCooldownSec?: number;
  phoneCooldownSec?: number;
  emailCooldownCode?: string;
  phoneCooldownCode?: string;

  emailVerifyCooldownSec?: number;
  phoneVerifyCooldownSec?: number;
  emailVerifyCooldownCode?: string;
  phoneVerifyCooldownCode?: string;

  formatMmSs?: (s: number) => string;

  onEmailResendCooldownFinished: () => void;
  onEmailVerifyCooldownFinished: () => void;
  onPhoneResendCooldownFinished: () => void;
  onPhoneVerifyCooldownFinished: () => void;
}

interface State {}

function fallbackFormatMmSs(totalSec: number) {
  const s = Math.max(0, Math.floor(totalSec));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  const ss2 = ss < 10 ? `0${ss}` : String(ss);
  return `${mm}:${ss2}`;
}

function CooldownAlert(props: {
  text: string;
  code?: string;
  cooldownSec: number;
  formatMmSs: (s: number) => string;
  onCooldownFinished: () => void;
}) {
  const { text, code, cooldownSec, formatMmSs, onCooldownFinished } = props;

  const [endAtMs, setEndAtMs] = React.useState<number>(() => Date.now() + Math.max(0, cooldownSec) * 1000);
  const [sec, setSec] = React.useState<number>(() => Math.max(0, Math.ceil((endAtMs - Date.now()) / 1000)));

  React.useEffect(() => {
    const s = Math.max(0, Number(cooldownSec || 0));
    const newEnd = Date.now() + s * 1000;
    setEndAtMs(newEnd);
    setSec(s);
  }, [cooldownSec]);

  React.useEffect(() => {
    const id = window.setInterval(() => {
      const left = Math.max(0, Math.ceil((endAtMs - Date.now()) / 1000));
      setSec(left);
      if (left <= 0) {
        window.clearInterval(id);
        onCooldownFinished();
      }
    }, 250);

    return () => window.clearInterval(id);
  }, [endAtMs, onCooldownFinished]);

  const isBlocked = code === 'AUTO_BLOCKED';
  const title = isBlocked ? 'Temporarily unavailable' : 'Please wait a moment';
  const subtitle = sec > 0 ? `Try again in ${sec} seconds.` : undefined;

  return (
    <div className={`eCooldownAlert ${isBlocked ? 'mBlocked' : 'mRateLimited'}`}>
      <div className="eCooldownAlertTop">
        <div className="eCooldownAlertLeft">
          <div className="eCooldownAlertIcon">
            <FontAwesomeIcon icon={faCircleExclamation} />
          </div>

          <div className="eCooldownAlertText">
            <div className="eCooldownAlertTitle">{title}</div>
            <div className="eCooldownAlertMessage">{text}</div>
            {subtitle && <div className="eCooldownAlertHint">{subtitle}</div>}
          </div>
        </div>

        {sec > 0 && <div className="eCooldownAlertTimer">{formatMmSs(sec)}</div>}
      </div>
    </div>
  );
}

export class VerificationForm extends React.Component<Props, State> {
  private emailContainerRef = React.createRef<HTMLDivElement>();
  private phoneContainerRef = React.createRef<HTMLDivElement>();
  private emailWidgetId: string | undefined = undefined;
  private phoneWidgetId: string | undefined = undefined;

  componentDidMount() {
    const sitekey = (window as any).turnstileSiteKey;
    if (!sitekey) return;

    const renderWidgets = () => {
      if (!(window as any).turnstile) return;
      if (this.emailContainerRef.current && this.emailWidgetId === undefined) {
        this.emailWidgetId = (window as any).turnstile.render(this.emailContainerRef.current, {
          sitekey,
          execution: 'execute',
          appearance: 'interaction-only',
          callback: (token: string) => {
            if ((window as any).__turnstileResolveFn_turnstile_resend_email) {
              (window as any).__turnstileResolveFn_turnstile_resend_email(token);
            }
          }
        });
        (window as any).__turnstileWidgetId_turnstile_resend_email = this.emailWidgetId;
      }
      if (this.phoneContainerRef.current && this.phoneWidgetId === undefined) {
        this.phoneWidgetId = (window as any).turnstile.render(this.phoneContainerRef.current, {
          sitekey,
          execution: 'execute',
          appearance: 'interaction-only',
          callback: (token: string) => {
            if ((window as any).__turnstileResolveFn_turnstile_resend_phone) {
              (window as any).__turnstileResolveFn_turnstile_resend_phone(token);
            }
          }
        });
        (window as any).__turnstileWidgetId_turnstile_resend_phone = this.phoneWidgetId;
      }
    };

    if ((window as any).turnstile) {
      renderWidgets();
    } else {
      (window as any).__pendingTurnstileInit = renderWidgets;
    }
  }

  componentWillUnmount() {
    (window as any).__pendingTurnstileInit = undefined;
    if ((window as any).turnstile) {
      if (this.emailWidgetId !== undefined) (window as any).turnstile.remove(this.emailWidgetId);
      if (this.phoneWidgetId !== undefined) (window as any).turnstile.remove(this.phoneWidgetId);
    }
  }
  render() {
    const {
      onCancel,
      onSubmit,

      email,
      phone,

      onSendEmailCodeAgainClick,
      onChangeEmailClick,
      onSendPhoneCodeAgainClick,
      onChangePhoneClick,

      isPhoneConfirmed,
      isEmailConfirmed,

      onSubmitEmailClick,
      onSubmitPhoneClick,

      emailResendError,
      emailVerifyError,
      phoneResendError,
      phoneVerifyError,

      emailCooldownSec,
      phoneCooldownSec,
      emailCooldownCode,
      phoneCooldownCode,

      emailVerifyCooldownSec,
      phoneVerifyCooldownSec,
      emailVerifyCooldownCode,
      phoneVerifyCooldownCode,

      formatMmSs,

      onEmailResendCooldownFinished,
      onEmailVerifyCooldownFinished,
      onPhoneResendCooldownFinished,
      onPhoneVerifyCooldownFinished
    } = this.props;

    const isPhoneVerificationRequired = !!phone;
    const isSubmitEnabled = !!isEmailConfirmed && (!isPhoneVerificationRequired || !!isPhoneConfirmed);

    const fmt = formatMmSs || fallbackFormatMmSs;

    const emailResendCd = Number(emailCooldownSec || 0) || 0;
    const emailVerifyCd = Number(emailVerifyCooldownSec || 0) || 0;
    const phoneResendCd = Number(phoneCooldownSec || 0) || 0;
    const phoneVerifyCd = Number(phoneVerifyCooldownSec || 0) || 0;

    return (
      <div className="eSchoolSignupVerification">
        <div className="eSchoolSignupScreenHeader">
          <h1>Confirm it's you</h1>
          <p>
            Enter the six-digit code sent to your email. If mobile verification is required, the SMS is sent after your
            email is confirmed.
          </p>
        </div>
        <div ref={this.emailContainerRef} />
        <div ref={this.phoneContainerRef} />

        <EmailVerificationForm
          onSendCodeAgainClick={onSendEmailCodeAgainClick}
          email={email}
          isConfirmed={isEmailConfirmed}
          onSubmit={onSubmitEmailClick}
          onChangeClick={onChangeEmailClick}
          resendCooldownSec={emailResendCd}
          verifyCooldownSec={emailVerifyCd}
          verifyErrorText={emailVerifyError}
          formatMmSs={fmt}
        />
        {emailResendError && emailResendCd > 0 && (
          <CooldownAlert
            text={emailResendError}
            code={emailCooldownCode}
            cooldownSec={emailResendCd}
            formatMmSs={fmt}
            onCooldownFinished={onEmailResendCooldownFinished}
          />
        )}

        {emailVerifyError && emailVerifyCd > 0 && (
          <CooldownAlert
            text={emailVerifyError}
            code={emailVerifyCooldownCode}
            cooldownSec={emailVerifyCd}
            formatMmSs={fmt}
            onCooldownFinished={onEmailVerifyCooldownFinished}
          />
        )}

        {isPhoneVerificationRequired ? (
          <PhoneVerificationForm
            onSendCodeAgainClick={onSendPhoneCodeAgainClick}
            phone={phone}
            isConfirmed={isPhoneConfirmed}
            onSubmit={onSubmitPhoneClick}
            onChangeClick={onChangePhoneClick}
            isEmailConfirmed={isEmailConfirmed}
            hintText={
              !isEmailConfirmed
                ? 'Please verify your email first. We’ll send your SMS code automatically once your email is confirmed.'
                : undefined
            }
            resendCooldownSec={phoneResendCd}
            verifyCooldownSec={phoneVerifyCd}
            verifyErrorText={phoneVerifyError}
            formatMmSs={fmt}
          />
        ) : (
          <div className="eVerificationNotRequired">Mobile verification is not required for this email domain.</div>
        )}

        {isPhoneVerificationRequired && phoneResendError && phoneResendCd > 0 && (
          <CooldownAlert
            text={phoneResendError}
            code={phoneCooldownCode}
            cooldownSec={phoneResendCd}
            formatMmSs={fmt}
            onCooldownFinished={onPhoneResendCooldownFinished}
          />
        )}

        {isPhoneVerificationRequired && phoneVerifyError && phoneVerifyCd > 0 && (
          <CooldownAlert
            text={phoneVerifyError}
            code={phoneVerifyCooldownCode}
            cooldownSec={phoneVerifyCd}
            formatMmSs={fmt}
            onCooldownFinished={onPhoneVerifyCooldownFinished}
          />
        )}

        <div className="eRegistrationFormActions">
          <Button onClick={onCancel} text={'Back'} customClass={'btn-secondary'} />
          <Button
            disabled={!isSubmitEnabled}
            onClick={onSubmit}
            text={['Continue', <FontAwesomeIcon key="arrow" icon={faArrowRight} />]}
            customClass={'eRegistrationSubmitButton'}
          />
        </div>
      </div>
    );
  }
}
